import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Star, RotateCcw, Volume2, Lightbulb, HelpCircle } from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const PuzzleGame = ({ 
  pieces = [],
  targetOrder = [],
  onComplete,
  title = "Put the pieces in order!",
  instructions = "Drag the pieces to arrange them correctly",
  showNumbers = true
}) => {
  const [shuffledPieces, setShuffledPieces] = useState([]);
  const [arrangedPieces, setArrangedPieces] = useState([]);
  const [draggedPiece, setDraggedPiece] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [selectedPiece, setSelectedPiece] = useState(null); // tap-to-select fallback for touch devices
  const [moves, setMoves] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    initializeGame();
  }, [pieces]);

  const initializeGame = () => {
    // Shuffle pieces
    const shuffled = [...pieces].sort(() => Math.random() - 0.5);
    setShuffledPieces(shuffled);
    setArrangedPieces(new Array(pieces.length).fill(null));
    setMoves(0);
    setCompleted(false);
    setShowHint(false);
  };

  const speakText = (text) => {
    _speakTTS(text, 0.85);
  };

  const handleDragStart = (piece, fromArranged = false, index = -1) => {
    setDraggedPiece({ piece, fromArranged, index });
    speakText(piece.label);
  };

  const handleDragOver = (index) => {
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (targetIndex) => {
    if (!draggedPiece) return;
    
    setMoves(m => m + 1);
    const { piece, fromArranged, index: sourceIndex } = draggedPiece;
    
    const newArranged = [...arrangedPieces];
    const newShuffled = [...shuffledPieces];
    
    // If dropping from shuffled to slot
    if (!fromArranged) {
      // If slot is already occupied, swap
      if (newArranged[targetIndex]) {
        newShuffled.push(newArranged[targetIndex]);
      }
      newArranged[targetIndex] = piece;
      const idx = newShuffled.findIndex(p => p.id === piece.id);
      if (idx > -1) newShuffled.splice(idx, 1);
    } else {
      // Moving from one slot to another
      if (newArranged[targetIndex]) {
        // Swap
        const temp = newArranged[targetIndex];
        newArranged[targetIndex] = piece;
        newArranged[sourceIndex] = temp;
      } else {
        newArranged[targetIndex] = piece;
        newArranged[sourceIndex] = null;
      }
    }
    
    setArrangedPieces(newArranged);
    setShuffledPieces(newShuffled);
    setDraggedPiece(null);
    setDragOverIndex(null);
    
    // Check if complete
    checkCompletion(newArranged);
  };

  const handleReturnToPool = () => {
    if (!draggedPiece || !draggedPiece.fromArranged) return;
    
    const { piece, index } = draggedPiece;
    const newArranged = [...arrangedPieces];
    const newShuffled = [...shuffledPieces, piece];
    
    newArranged[index] = null;
    setArrangedPieces(newArranged);
    setShuffledPieces(newShuffled);
    setDraggedPiece(null);
  };

  // ============================================================
  // Tap-to-place fallback so drag & drop works on mobile / tablet
  // where native HTML5 drag events don't fire on touch.
  // ============================================================
  const placeSelectedIntoSlot = (piece, sourceIsSlot, sourceIndex, targetSlotIndex) => {
    const newArranged = [...arrangedPieces];
    const newShuffled = [...shuffledPieces];

    if (!sourceIsSlot) {
      if (newArranged[targetSlotIndex]) newShuffled.push(newArranged[targetSlotIndex]);
      newArranged[targetSlotIndex] = piece;
      const idx = newShuffled.findIndex((p) => p.id === piece.id);
      if (idx > -1) newShuffled.splice(idx, 1);
    } else {
      if (newArranged[targetSlotIndex]) {
        const temp = newArranged[targetSlotIndex];
        newArranged[targetSlotIndex] = piece;
        newArranged[sourceIndex] = temp;
      } else {
        newArranged[targetSlotIndex] = piece;
        newArranged[sourceIndex] = null;
      }
    }

    setArrangedPieces(newArranged);
    setShuffledPieces(newShuffled);
    setMoves((m) => m + 1);
    checkCompletion(newArranged);
  };

  const handlePieceTap = (piece, fromSlot = false, slotIndex = -1) => {
    speakText(piece.label);
    if (selectedPiece && selectedPiece.piece.id === piece.id) {
      setSelectedPiece(null); // deselect on second tap
      return;
    }
    if (selectedPiece && selectedPiece.fromSlot && fromSlot) {
      // Swap two slots
      const source = selectedPiece;
      const newArranged = [...arrangedPieces];
      newArranged[source.slotIndex] = piece;
      newArranged[slotIndex] = source.piece;
      setArrangedPieces(newArranged);
      setMoves((m) => m + 1);
      setSelectedPiece(null);
      checkCompletion(newArranged);
      return;
    }
    setSelectedPiece({ piece, fromSlot, slotIndex });
  };

  const handleSlotTap = (slotIndex) => {
    const currentInSlot = arrangedPieces[slotIndex];
    if (!selectedPiece) {
      if (currentInSlot) handlePieceTap(currentInSlot, true, slotIndex);
      return;
    }
    placeSelectedIntoSlot(selectedPiece.piece, selectedPiece.fromSlot, selectedPiece.slotIndex, slotIndex);
    setSelectedPiece(null);
  };

  const handleReturnSelectedToPool = () => {
    if (!selectedPiece || !selectedPiece.fromSlot) return;
    const newArranged = [...arrangedPieces];
    newArranged[selectedPiece.slotIndex] = null;
    setArrangedPieces(newArranged);
    setShuffledPieces([...shuffledPieces, selectedPiece.piece]);
    setMoves((m) => m + 1);
    setSelectedPiece(null);
  };

  const checkCompletion = (arranged) => {
    // Check if all slots are filled
    if (arranged.some(p => p === null)) return;
    
    // Check if order is correct
    const isCorrect = arranged.every((piece, index) => 
      piece.id === targetOrder[index]
    );
    
    if (isCorrect) {
      setCompleted(true);
      toast.success('Perfect! You solved the puzzle!');
      const score = Math.max(100 - (moves * 2), 60);
      if (onComplete) {
        onComplete(score, moves);
      }
    }
  };

  const getHint = () => {
    // Find first incorrect position
    for (let i = 0; i < arrangedPieces.length; i++) {
      if (!arrangedPieces[i] || arrangedPieces[i].id !== targetOrder[i]) {
        const correctPiece = pieces.find(p => p.id === targetOrder[i]);
        return `Position ${i + 1} should be "${correctPiece?.label}"`;
      }
    }
    return "You're on the right track!";
  };

  if (completed) {
    const score = Math.max(100 - (moves * 2), 60);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
        data-testid="puzzle-complete"
      >
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 flex items-center justify-center">
          <Star className="w-12 h-12 text-white" />
        </div>
        <h2 className="font-fredoka text-3xl font-bold text-cyan-800 mb-2">Puzzle Solved!</h2>
        <p className="text-xl text-cyan-600 mb-2">Score: {score}%</p>
        <p className="text-lg text-slate-500 mb-6">Completed in {moves} moves</p>
        <Button onClick={initializeGame} variant="outline" className="gap-2" data-testid="play-again-btn">
          <RotateCcw className="w-4 h-4" /> Play Again
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6" data-testid="puzzle-game">
      {/* Header */}
      <div className="text-center">
        <h3 className="font-fredoka text-2xl font-bold text-slate-800 mb-2">{title}</h3>
        <p className="text-slate-600">{instructions}</p>
        <div className="flex justify-center gap-4 mt-2">
          <span className="text-sm text-slate-500">Moves: {moves}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHint(!showHint)}
            className="text-amber-600"
            data-testid="hint-btn"
          >
            <Lightbulb className="w-4 h-4 mr-1" />
            Hint
          </Button>
        </div>
        {showHint && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-amber-600 bg-amber-50 rounded-lg p-2"
          >
            {getHint()}
          </motion.p>
        )}
      </div>

      {/* Puzzle Slots */}
      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6">
        <p className="text-sm text-cyan-700 mb-4 text-center font-medium">
          {showNumbers ? 'Arrange in order (1, 2, 3...)' : 'Complete the sequence'}
        </p>
        {selectedPiece && (
          <p className="text-xs text-center text-cyan-600 mb-2 animate-pulse">
            Tap a slot to place &ldquo;{selectedPiece.piece.label}&rdquo;
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          {arrangedPieces.map((piece, index) => (
            <motion.div
              key={`slot-${index}`}
              className={`w-24 h-20 rounded-xl border-3 border-dashed flex flex-col items-center justify-center transition-all cursor-pointer ${
                dragOverIndex === index || (selectedPiece && !piece)
                  ? 'border-cyan-400 bg-cyan-100 scale-105'
                  : piece
                    ? 'border-cyan-300 bg-white'
                    : 'border-slate-300 bg-slate-50'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                handleDragOver(index);
              }}
              onDragLeave={handleDragLeave}
              onDrop={() => handleDrop(index)}
              onClick={() => handleSlotTap(index)}
              data-testid={`slot-${index}`}
            >
              {showNumbers && (
                <span className="text-xs text-slate-400 mb-1">{index + 1}</span>
              )}
              {piece ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  draggable
                  onDragStart={() => handleDragStart(piece, true, index)}
                  onClick={(e) => { e.stopPropagation(); handlePieceTap(piece, true, index); }}
                  className={`px-3 py-2 text-white rounded-lg font-bold cursor-grab active:cursor-grabbing text-sm ${
                    selectedPiece && selectedPiece.piece.id === piece.id ? 'bg-amber-500 ring-2 ring-amber-300' : 'bg-cyan-500'
                  }`}
                >
                  {piece.label}
                </motion.div>
              ) : (
                <HelpCircle className="w-6 h-6 text-slate-300" />
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Available Pieces */}
      {shuffledPieces.length > 0 && (
        <div 
          className="bg-slate-50 rounded-2xl p-6"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleReturnToPool}
          onClick={handleReturnSelectedToPool}
        >
          <p className="text-sm text-slate-500 mb-4 text-center">Tap a piece then tap a slot (or drag on desktop):</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <AnimatePresence>
              {shuffledPieces.map((piece) => (
                <motion.div
                  key={piece.id}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  draggable
                  onDragStart={() => handleDragStart(piece, false)}
                  className={`px-5 py-3 rounded-xl font-bold cursor-grab active:cursor-grabbing select-none transition-all ${
                    (draggedPiece?.piece?.id === piece.id) || (selectedPiece?.piece?.id === piece.id)
                      ? 'bg-cyan-500 text-white scale-110 shadow-lg ring-2 ring-cyan-300'
                      : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-cyan-300 hover:shadow-md'
                  }`}
                  onClick={(e) => { e.stopPropagation(); handlePieceTap(piece, false); }}
                  data-testid={`piece-${piece.id}`}
                >
                  {piece.label}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Reset Button */}
      <div className="text-center">
        <Button variant="outline" size="sm" onClick={initializeGame} className="gap-2" data-testid="reset-btn">
          <RotateCcw className="w-4 h-4" /> Start Over
        </Button>
      </div>
    </div>
  );
};

export default PuzzleGame;
